'use client'
import {
  Anchor,
  Breadcrumbs,
  Button,
  Flex,
  Skeleton,
  Text
} from '@mantine/core'
import { RiArrowLeftSLine } from '@remixicon/react'
import dynamic from 'next/dynamic'
import { useRouter, useSearchParams } from 'next/navigation'
import { memo } from 'react'

const RiShareLine = dynamic(
  () => import('react-icons/ri').then((mod) => mod.RiShareLine),
  { ssr: false },
)

interface BreadcrumbProps {
  url?: string
  data: {
    data?: {
      city?: string | null
    } | null
  } | null
  loading: boolean
  breadcrumbItems?: { title: string; href: string }[]
}

const BreadCums = ({
  url,
  data,
  loading,
  breadcrumbItems,
}: BreadcrumbProps) => {
  const router = useRouter()
  const searchParams = useSearchParams()
  // Normalise an empty `location=` to null so a value-less param doesn't get
  // treated as a real "came from search" signal below.
  const location = searchParams.get('location') || null

  const handleShare = async () => {
    if (typeof window !== 'undefined') {
      const shareData: ShareData = {
        title: 'Check this out!',
        text: 'I found something interesting for you.',
        url: window.location.href,
      }

      if (navigator.share) {
        try {
          await navigator.share(shareData)
        } catch (error) {
          console.error('Sharing failed:', error)
        }
      } else {
        try {
          await navigator.clipboard.writeText(window.location.href)
          alert('Link copied to clipboard!')
        } catch (error) {
          console.error('Failed to copy:', error)
        }
      }
    } else {
      console.error('window is undefined')
    }
  }

  if (loading || !data) {
    return (
      <Flex
        align={{ md: 'center', base: 'flex-start' }}
        direction={{ base: 'column', md: 'row' }}
        justify={{ md: 'space-between' }}
        display={{ base: 'flex', md: 'flex' }}
        gap={{ m: 'xs', base: '20px' }}
        ml={{ base: '15px', md: 0 }}
        mb='md'
        w='98%'
        mt={{ md: 0, base: '15px' }}
        h={{ md: 38, base: 90 }}
        style={{
          fontSize: '14px',
          fontWeight: 'normal',
          color: 'black',
          position: 'relative',
          zIndex: 40,
        }}
      >
        {/* Left: back icon + label */}
        <Flex align='center' gap={12}>
          <Skeleton height={28} width={28} radius='xl' />
          <Skeleton height={14} width={72} radius='sm' />
        </Flex>

        {/* Right: share button */}
        <Skeleton height={38} width={120} radius={8} />
      </Flex>
    )
  }

  return (
    <Flex
      align={{ md: 'center', base: 'flex-start' }}
      direction={{ base: 'column', md: 'row' }}
      justify={{ md: 'space-between' }}
      display={{ base: 'flex', md: 'flex' }}
      gap={{ m: 'xs', base: '20px' }}
      ml={{ base: '15px', md: 0 }}
      mt={{ md: 0, base: '15px' }}
      mb='md'
      w='100%'
      h={{ md: 38, base: 90 }}
      fw={500}
      fs={'14px'}
      pos={'relative'}
      color='black'
      style={{
        zIndex: 40,
      }}
    >
      <Flex align='center' gap='md'>
        <RiArrowLeftSLine
          className='w-7 h-7 rounded-full cursor-pointer bg-[#191919] text-white'
          onClick={() => {
            if (!url || location !== null) {
              router.back()
              return
            }

            router.push(url)
          }}
          aria-label='Go back'
        />

        {breadcrumbItems && breadcrumbItems.length > 0 ? (
          <Breadcrumbs>
            {breadcrumbItems.map((item, index) => (
              <Anchor
                key={index}
                href={item.href}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault()
                  router.push(item.href)
                }}
              >
                {item.title}
              </Anchor>
            ))}
          </Breadcrumbs>
        ) : (
          (() => {
            const cityLabel = location ?? data?.data?.city ?? null
            return url && cityLabel ? (
              <button
                type='button'
                onClick={() => router.push(`/property-listing?location=${encodeURIComponent(cityLabel)}`)}
                className='font-semibold text-[14px] leading-[150%] text-[#191919] hover:underline underline-offset-2 cursor-pointer'
              >
                Property in {cityLabel}
              </button>
            ) : (
              <Text
                component='span'
                fw={600}
                fz={14}
                lh='150%'
                c='#191919'
                style={{ letterSpacing: '0%' }}
              >
                {url ? 'Home' : 'Back'}
              </Text>
            )
          })()
        )}
      </Flex>

      <Button
        h={38}
        radius={8}
        bg='#F5F5F5'
        c='#191919'
        px={16}
        py={8}
        leftSection={<RiShareLine size={16} />}
        onClick={handleShare}
        suppressHydrationWarning
        aria-label='Share this page'
      >
        Share
      </Button>
    </Flex>
  )
}

export default memo(BreadCums)
